export type MaybePromise<T> = T | Promise<T>

export type QueryPrimitive = string | number | boolean | null | undefined
export type QueryValue = QueryPrimitive | readonly QueryPrimitive[]
export type QueryParams = URLSearchParams | Record<string, QueryValue>

export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export type BearerTokenProvider = () => MaybePromise<string | null | undefined>

export type ParseContext = {
  response: Response
  url: string
}

export type ResponseParser<T> = (value: unknown, context: ParseContext) => MaybePromise<T>

export type ApiRequestOptions<T> = {
  method?: string
  query?: QueryParams
  headers?: HeadersInit
  body?: unknown
  /**
   * Convert the decoded response body into the caller's type. This is where
   * consumers can plug Zod v3/v4 `.parse`, a safeParse wrapper, or a handwritten
   * guard without sharing one schema-library instance across apps.
   */
  parse?: ResponseParser<T>
  /** Set false for public endpoints when the client has a token provider. */
  auth?: boolean
  signal?: AbortSignal
  /** Extra fetch init fields that are not covered by the typed helpers above. */
  init?: Omit<RequestInit, "body" | "headers" | "method" | "signal">
  /** Per-request fetch override, useful in tests. */
  fetch?: FetchLike
}

export type ApiClientOptions = {
  baseUrl?: string
  headers?: HeadersInit | (() => MaybePromise<HeadersInit | undefined>)
  getBearerToken?: BearerTokenProvider
  fetch?: FetchLike
}

export type ApiClient = {
  request<T = unknown>(path: string, options?: ApiRequestOptions<T>): Promise<T>
  get<T = unknown>(path: string, options?: Omit<ApiRequestOptions<T>, "body" | "method">): Promise<T>
  post<T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions<T>, "body" | "method">): Promise<T>
  put<T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions<T>, "body" | "method">): Promise<T>
  patch<T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions<T>, "body" | "method">): Promise<T>
  delete<T = unknown>(path: string, options?: Omit<ApiRequestOptions<T>, "body" | "method">): Promise<T>
}

export class ApiClientError extends Error {
  override readonly name = "ApiClientError"
  readonly status: number
  readonly statusText: string
  readonly url: string
  readonly headers: Headers
  readonly body: unknown

  constructor(response: Response, body: unknown, url: string) {
    super(`HTTP ${response.status} ${response.statusText} for ${url}`)
    this.status = response.status
    this.statusText = response.statusText
    this.url = url
    this.headers = new Headers(response.headers)
    this.body = body
  }
}

export const createApiClient = (options: ApiClientOptions = {}): ApiClient => {
  const request = async <T = unknown>(path: string, requestOptions: ApiRequestOptions<T> = {}): Promise<T> => {
    const fetchImpl = requestOptions.fetch ?? options.fetch ?? globalThis.fetch
    if (!fetchImpl) throw new Error("No fetch implementation available")

    const url = buildUrl(options.baseUrl, path, requestOptions.query)
    const headers = new Headers(await resolveHeaders(options.headers))
    mergeHeaders(headers, requestOptions.headers)

    if (requestOptions.auth !== false && options.getBearerToken) {
      const token = await options.getBearerToken()
      if (token) headers.set("authorization", token.toLowerCase().startsWith("bearer ") ? token : `Bearer ${token}`)
    }

    const init = buildRequestInit(requestOptions, headers)
    const response = await fetchImpl(url, init)
    const decoded = await decodeResponseBody(response)

    if (!response.ok) throw new ApiClientError(response, decoded, url)

    if (requestOptions.parse) return requestOptions.parse(decoded, { response, url })
    return decoded as T
  }

  return {
    request,
    get: (path, opts) => request(path, { ...opts, method: "GET" }),
    post: (path, body, opts) => request(path, { ...opts, method: "POST", body }),
    put: (path, body, opts) => request(path, { ...opts, method: "PUT", body }),
    patch: (path, body, opts) => request(path, { ...opts, method: "PATCH", body }),
    delete: (path, opts) => request(path, { ...opts, method: "DELETE" }),
  }
}

export const jsonParser = <T>(parse: ResponseParser<T>): ResponseParser<T> => parse

const resolveHeaders = async (
  headers: ApiClientOptions["headers"],
): Promise<HeadersInit | undefined> => (typeof headers === "function" ? headers() : headers)

const mergeHeaders = (target: Headers, source?: HeadersInit): void => {
  if (!source) return
  new Headers(source).forEach((value, key) => target.set(key, value))
}

const buildRequestInit = <T>(options: ApiRequestOptions<T>, headers: Headers): RequestInit => {
  const body = encodeBody(options.body, headers)
  return {
    ...options.init,
    method: options.method ?? (body === undefined ? "GET" : "POST"),
    headers,
    body,
    signal: options.signal,
  }
}

const encodeBody = (body: unknown, headers: Headers): BodyInit | null | undefined => {
  if (body === undefined) return undefined
  if (body === null) return null
  if (isBodyInit(body)) return body

  if (!headers.has("content-type")) headers.set("content-type", "application/json")
  return JSON.stringify(body)
}

const isBodyInit = (body: unknown): body is BodyInit => {
  if (typeof body === "string") return true
  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) return true
  if (typeof FormData !== "undefined" && body instanceof FormData) return true
  if (typeof Blob !== "undefined" && body instanceof Blob) return true
  if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) return true
  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(body)) return true
  if (typeof ReadableStream !== "undefined" && body instanceof ReadableStream) return true
  return false
}

const decodeResponseBody = async (response: Response): Promise<unknown> => {
  if (response.status === 204 || response.status === 205) return undefined

  const contentLength = response.headers.get("content-length")
  if (contentLength === "0") return undefined

  const contentType = response.headers.get("content-type") ?? ""
  const text = await response.text()
  if (!text) return undefined

  if (contentType.includes("application/json") || contentType.includes("+json")) {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }

  return text
}

const buildUrl = (baseUrl: string | undefined, path: string, query?: QueryParams): string => {
  const hasAbsolutePath = /^[a-z][a-z\d+.-]*:/i.test(path)
  const hasAbsoluteBase = baseUrl ? /^[a-z][a-z\d+.-]*:/i.test(baseUrl) : false

  if (hasAbsolutePath || hasAbsoluteBase) {
    const url = hasAbsolutePath ? new URL(path) : new URL(stripLeadingSlash(path), withTrailingSlash(baseUrl ?? ""))
    appendQuery(url.searchParams, query)
    return url.toString()
  }

  return appendQueryToRelativePath(joinRelativeBase(baseUrl, path), query)
}

const appendQuery = (params: URLSearchParams, query?: QueryParams): void => {
  if (!query) return

  if (query instanceof URLSearchParams) {
    query.forEach((value, key) => params.append(key, value))
    return
  }

  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value]
    for (const item of values) {
      if (item === null || item === undefined) continue
      params.append(key, String(item))
    }
  }
}

const appendQueryToRelativePath = (path: string, query?: QueryParams): string => {
  const [pathname = "", search = ""] = path.split("?", 2)
  const params = new URLSearchParams(search)
  appendQuery(params, query)
  const qs = params.toString()
  return qs ? `${pathname}?${qs}` : pathname
}

const joinRelativeBase = (baseUrl: string | undefined, path: string): string => {
  if (!baseUrl) return path
  if (!path) return baseUrl
  return `${baseUrl.replace(/\/+$/, "")}/${stripLeadingSlash(path)}`
}

const stripLeadingSlash = (value: string): string => value.replace(/^\/+/, "")
const withTrailingSlash = (value: string): string => (value.endsWith("/") ? value : `${value}/`)
